import { RepositoryError } from "@invoiceddd/domain";
import { Context, Data, Effect, Layer } from "effect";
import { InvoiceNumberRepository } from "../ports/InvoiceNumberRepository";

export class InvoiceNumberConflictError extends Data.TaggedError("@/InvoiceNumberConflictError")<{
  readonly cause?: unknown;
  readonly message: string;
  readonly affectedInvoiceNumber: string;
}> {}

/**
 * Domain service for validating invoice numbers are unique.
 * This requires I/O and thus must not be used within the Domain.
 *
 * Use it like this:
 * - In the application layer, get the Repository implementation
 * - Provide it this service
 * - Call it and use the result.
 */
export interface InvoiceNumberUniqueCheckerSchema {
  /**
   * Validates invoice number uniqueness
   *
   * @param invoiceNumber - The invoice number to validate
   * @returns Effect that succeeds if valid (true), fails with appropriate error if invalid
   */
  readonly validate: (
    // eslint-disable-next-line no-unused-vars
    invoiceNumber: string
  ) => Effect.Effect<true, InvoiceNumberConflictError | RepositoryError>;
}

/**
 * Context tag for InvoiceNumberUniqueChecker service
 */
export const InvoiceNumberUniqueChecker = Context.GenericTag<InvoiceNumberUniqueCheckerSchema>(
  "@/InvoiceNumberUniqueChecker"
);

/**
 * Implementation of InvoiceNumberUniqueChecker service
 */
export const InvoiceNumberUniqueCheckerLive = Layer.effect(
  InvoiceNumberUniqueChecker,
  Effect.gen(function* () {
    const repository = yield* InvoiceNumberRepository;

    const implementation: InvoiceNumberUniqueCheckerSchema = {
      validate: (invoiceNumber: string) => {
        return Effect.gen(function* () {
          const exists = yield* repository.existsByInvoiceNumber(invoiceNumber);

          if (exists) {
            yield* Effect.annotateCurrentSpan({
              isUnique: false,
            });
            return yield* Effect.fail(
              new InvoiceNumberConflictError({
                message: `Invoice number '${invoiceNumber}' already exists`,
                affectedInvoiceNumber: invoiceNumber,
              })
            );
          }

          yield* Effect.annotateCurrentSpan({
            isUnique: true,
          });
          return true as const;
        }).pipe(
          Effect.withSpan("InvoiceNumberUniqueChecker.validate", {
            attributes: {
              invoiceNumber,
            },
          })
        );
      },
    };
    return InvoiceNumberUniqueChecker.of(implementation);
  })
);
